/**
 * Display formatting shared by every screen: money, machine sizes and time.
 *
 * These live together because getting any of them wrong is quietly misleading
 * rather than loudly broken — a price short by a factor of a hundred or an "an
 * hour ago" that is really "two hours ago" both look completely fine on screen.
 */

/**
 * Parse a timestamp as the API actually sends it.
 *
 * The pool runs with `dateStrings: true`, so a DATETIME(3) arrives as
 * "2026-08-22 18:03:11.123" — UTC, but with nothing on the string that says so.
 * `new Date()` on that non-ISO shape is implementation-defined and every major
 * engine reads it as *local* time, which would skew every "created X ago" on the
 * page by the viewer's UTC offset. So the marker is added before parsing.
 */
export function parseApiDate(value: string): Date | null {
  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value);
  const date = new Date(hasZone ? value : `${value.replace(' ', 'T')}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

const RELATIVE_UNITS: ReadonlyArray<readonly [Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 31_536_000],
  ['month', 2_592_000],
  ['week', 604_800],
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
];

const relative = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

/** "just now", "9 minutes ago", "yesterday", "3 months ago". */
export function relativeTime(value: string, now: number = Date.now()): string {
  const date = parseApiDate(value);
  if (!date) return 'unknown';

  const seconds = Math.round((now - date.getTime()) / 1000);
  // Server and browser clocks disagree by a second or two routinely; a future
  // timestamp is that, not a machine created tomorrow.
  if (seconds < 45) return 'just now';

  for (const [unit, size] of RELATIVE_UNITS) {
    if (seconds >= size) return relative.format(-Math.round(seconds / size), unit);
  }
  return 'just now';
}

/** The full timestamp, for the `title` of an element showing a relative one. */
export function absoluteTime(value: string): string {
  const date = parseApiDate(value);
  return date ? date.toLocaleString() : value;
}

/**
 * Money from integer minor units. Whole amounts lose their ".00" — a grid of
 * plan prices reads faster without a column of identical zeroes — and an
 * unexpected currency code falls back rather than throwing a RangeError in the
 * middle of a render.
 */
export function formatPrice(cents: number, currency: string): string {
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

/** 1024 -> "1 GB", 1536 -> "1.5 GB", 512 -> "512 MB". */
export function formatRam(megabytes: number): string {
  if (megabytes < 1024) return `${megabytes} MB`;
  const gb = megabytes / 1024;
  return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
}

export function formatDisk(gigabytes: number): string {
  return `${gigabytes} GB`;
}

export function formatBandwidth(terabytes: number): string {
  return `${terabytes} TB`;
}

/**
 * A byte count for a person.
 *
 * Decimal units, because the allowance is sold in decimal terabytes and a meter
 * that reported gibibytes against a terabyte allowance would not add up on the
 * page it is printed on.
 *
 * One decimal below 100 and none above it: "1.4 GB" is a number somebody reads,
 * "1.437 GB" is one they have to parse, and at 400 GB the tenth is noise.
 */
export function formatBytes(bytes: number): string {
  const units: Array<[number, string]> = [
    [1_000_000_000_000, 'TB'],
    [1_000_000_000, 'GB'],
    [1_000_000, 'MB'],
    [1_000, 'kB'],
  ];
  for (const [size, unit] of units) {
    if (bytes >= size) {
      const value = bytes / size;
      return `${value >= 100 ? Math.round(value) : Math.round(value * 10) / 10} ${unit}`;
    }
  }
  return `${Math.round(bytes)} B`;
}

/** Cents as euros, for a figure that ends up beside an invoice. */
export function formatCents(cents: number): string {
  return `€${(cents / 100).toFixed(2)}`;
}

export function formatVcpu(vcpu: number): string {
  return `${vcpu} vCPU`;
}

/** "a plan, an OS and a hostname" — for telling someone what is still missing. */
export function joinList(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * How long a machine has been up, from a count of seconds.
 *
 * Two units at most, largest first: "6d 4h", "4h 12m", "12m". Three would be
 * false precision on a figure that is already a few seconds stale by the time
 * it is painted, and one loses the part people actually read — "6d" and "6d 23h"
 * are a day apart.
 *
 * Under a minute reads as "just started", because a machine that has been up
 * for 40 seconds has almost always just been rebooted, and that is the fact the
 * reader wants rather than the number.
 */
export function formatUptime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return 'unknown';
  if (seconds < 60) return 'just started';

  const d = Math.floor(seconds / 86_400);
  const h = Math.floor((seconds % 86_400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);

  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  return `${m}m`;
}
